import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import { PageTransition } from './components/PageTransition';
import { BackgroundBeams } from './components/ui/background-beams';
import { DonateByMailProvider, useDonateByMail } from './contexts/DonateByMailContext';
// Lazy load page components
const HomePage = lazy(() => import('./pages/HomePage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const ProgramsPage = lazy(() => import('./pages/ProgramsPage'));
const TeamPage = lazy(() => import('./pages/TeamPage'));
const TrainingCenterPage = lazy(() => import('./pages/TrainingCenterPage'));
const RoadmapPage = lazy(() => import('./pages/RoadmapPage'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const ContactPage = lazy(() => import('./pages/ContactPage'));
// Loading component
const PageLoader = () => <div className="flex items-center justify-center min-h-screen bg-porcelain" data-id="element-0" data-mp="3ijpdn24eb1aa">
    <div className="animate-pulse text-israel-blue text-xl" data-id="element-1" data-mp="3mk83iqmq9x9u">Loading...</div>
  </div>;
// Scroll to top component
function ScrollToTop() {
  const {
    pathname
  } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}
function AnimatedRoutes() {
  const location = useLocation();
  return <AnimatePresence mode="wait" data-id="element-2" data-mp="2zn6cayzu2hoa">
      <Routes location={location} key={location.pathname} data-id="element-3" data-mp="33m5am0r27g6r">
        <Route path="/" element={<Suspense fallback={<PageLoader data-id="element-6" data-mp="38k6u2msedhir" />} data-id="element-5" data-mp="38kqd66tsabyd">
              <PageTransition data-id="element-7" data-mp="38o4h3ji3it7x">
                <HomePage data-id="element-8" data-mp="3dle30p6a0mr0" />
              </PageTransition>
            </Suspense>} data-id="element-4" data-mp="33orik1hv3xpi" />
        <Route path="/about" element={<Suspense fallback={<PageLoader data-id="element-11" data-mp="sunfgec14qjr" />} data-id="element-10" data-mp="nxn902b9pls4">
              <PageTransition data-id="element-12" data-mp="stubfl4ggf5b">
                <AboutPage data-id="element-13" data-mp="sssqmoxn0eae" />
              </PageTransition>
            </Suspense>} data-id="element-9" data-mp="nw2cw89b1us5" />
        <Route path="/programs" element={<Suspense fallback={<PageLoader data-id="element-16" data-mp="3ubzvsgflbtha" />} data-id="element-15" data-mp="3udkqq75thefo">
              <PageTransition data-id="element-17" data-mp="3uf5ixq4nslad">
                <ProgramsPage data-id="element-18" data-mp="d57h17dyqql6" />
              </PageTransition>
            </Suspense>} data-id="element-14" data-mp="34nbhsjeuzx5" />
        <Route path="/team" element={<Suspense fallback={<PageLoader data-id="element-21" data-mp="3gleakl2nz7g8" />} data-id="element-20" data-mp="83i6rtykegy5">
              <PageTransition data-id="element-22" data-mp="3gmfw5jhb3cc7">
                <TeamPage data-id="element-23" data-mp="3glo0k8nplelx" />
              </PageTransition>
            </Suspense>} data-id="element-19" data-mp="851n1kk80rzx" />
        <Route path="/training-center" element={<Suspense fallback={<PageLoader data-id="element-26" data-mp="3qcqzb3t61hi1" />} data-id="element-25" data-mp="3qc950cq2ddcr">
              <PageTransition data-id="element-27" data-mp="3qd0ud5t2ieul">
                <TrainingCenterPage data-id="element-28" data-mp="3lk7mwa67sset" />
              </PageTransition>
            </Suspense>} data-id="element-24" data-mp="3bo4mp9uxb3ui" />
        <Route path="/roadmap" element={<Suspense fallback={<PageLoader data-id="element-31" data-mp="2swbuvirlbood" />} data-id="element-30" data-mp="2svtmjnrnlk03">
              <PageTransition data-id="element-32" data-mp="2sv0qbgqbmcw7">
                <RoadmapPage data-id="element-33" data-mp="2stpjf81kwp0v" />
              </PageTransition>
            </Suspense>} data-id="element-29" data-mp="2xtv609szrlc3" />
        <Route path="/support" element={<Suspense fallback={<PageLoader data-id="element-36" data-mp="1d5ss57vjenz3" />} data-id="element-35" data-mp="1d59jyivovdy9">
              <PageTransition data-id="element-37" data-mp="yflf57oatx5s">
                <SupportPage data-id="element-38" data-mp="ygmuhoccl5tb" />
              </PageTransition>
            </Suspense>} data-id="element-34" data-mp="18byb6c7bgn3p" />
        <Route path="/contact" element={<Suspense fallback={<PageLoader data-id="element-41" data-mp="1r3r11xr39s77" />} data-id="element-40" data-mp="1r4969jvqbgr9">
              <PageTransition data-id="element-42" data-mp="1r4sfmc7bcwto">
                <ContactPage data-id="element-43" data-mp="1r2g0s7wur2ku" />
              </PageTransition>
            </Suspense>} data-id="element-39" data-mp="13fpz57of1a0r" />
      </Routes>
    </AnimatePresence>;
}
function StickyDonateButtons() {
  const {
    openDonateByMail
  } = useDonateByMail();
  return <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-50 flex items-center gap-2" data-id="element-44" data-mp="1m4ei3o3c959w">
      <button type="button" onClick={openDonateByMail} className="px-4 sm:px-5 py-3 bg-white text-israel-blue font-semibold rounded-full border border-israel-blue/20 hover:bg-gray-50 transition-colors duration-200 shadow-lg whitespace-nowrap text-sm sm:text-base" data-id="element-45" data-mp="6nw43uhcibbz">
        Donate by Mail
      </button>
      <a href="https://donate.stripe.com/7sY5kw7P1eO24x90y314401" target="_blank" rel="noopener noreferrer" className="px-4 sm:px-6 py-3 bg-israel-blue text-white font-semibold rounded-full hover:bg-israel-blue-light transition-colors duration-200 shadow-lg whitespace-nowrap text-sm sm:text-base" data-id="element-46" data-mp="m4uid13epckk">
        Donate Online
      </a>
    </div>;
}
export function App() {
  return <BrowserRouter data-id="element-47" data-mp="14uu0f40txreh">
      <DonateByMailProvider data-id="element-48" data-mp="zxttys02immu">
        <div className="w-full min-h-screen relative text-gray-800 font-display bg-porcelain" data-id="element-49" data-mp="zx27q7sd2198">
          <BackgroundBeams data-id="element-50" data-mp="vx059wesx3sw" />
          <div className="relative z-10" data-id="element-51" data-mp="vvp2nzx3e23d">
            <ScrollToTop data-id="element-52" data-mp="3ko2oef2y8kqe" />
            <Navigation data-id="element-53" data-mp="3kob3mdmjo24r" />
            <AnimatedRoutes data-id="element-54" data-mp="3kpm7edfzp9vv" />
            <Footer data-id="element-55" data-mp="3klq25pq5ro7j" />
            <StickyDonateButtons data-id="element-56" data-mp="3gmpo0kiml9qv" />
          </div>
        </div>
      </DonateByMailProvider>
    </BrowserRouter>;
}